import { call, put, select, takeEvery } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { formatISO } from "date-fns";

import { supabase } from "@/lib/supabase";
import {
  fetchPrompts,
  fetchPromptsSuccess,
  fetchPromptsFailure,
  addPrompt,    
  deletePrompt,
  addPromptSuccess,
  deletePromptSuccess,
  deletePromptFailure,
  addPromptFailure,
  changePromptStatus,
  changePromptStatusFailure,
} from "./slice";
import {
  AddPromptPayload,
  ChangePromptStatusPayload,
  DeletePromptPayload
} from "./types";
import { selectAuthAccountId } from "../selectors/auth";
import { applySagaError, isFiniteBrandId, reserchScoresSaga } from "../utils/saga";
import {
  DOMAIN_ERROR_CODES,
  DomainError,
  throwOnSupabaseError,
} from "../../utils/errors";
import { PROMPTS_TABLE_NAME } from "../const";
import { selectSelectedBrandIdState } from "../selectors/brands";
import analytics from "@/lib/Analytics";

export function* fetchPromptsSaga() {
  try {
    const userID: string | null = yield select(selectAuthAccountId);
    const selectedBrandId: number | undefined = yield select(selectSelectedBrandIdState);

    if (!userID || !isFiniteBrandId(selectedBrandId)) return;

    const { data, error } = yield call(() =>
      supabase
        .from(PROMPTS_TABLE_NAME)
        .select('*')
        .eq('user_id', userID)
        .eq('brand_id', selectedBrandId)
    );

    throwOnSupabaseError(error);

    yield put(fetchPromptsSuccess(data));

  } catch (error) {
    yield* applySagaError(error, {
      operationName: "prompts.fetchPrompts",
      failureAction: fetchPromptsFailure,
    });
  }
}

export function* addPromptSaga(action: PayloadAction<AddPromptPayload>) {
  try {
    const { content } = action.payload;
    const userId: string | null = yield select(selectAuthAccountId);

    if (!userId) {
      throw new DomainError(
        DOMAIN_ERROR_CODES.VALIDATION_FAILED,
        "User ID is required",
      );
    }
    const selectedBrandId: number | undefined = yield select(selectSelectedBrandIdState);

    const { data, error } = yield call(() =>
      supabase
        .from(PROMPTS_TABLE_NAME)
        .insert({
          content,
          user_id: userId,
          brand_id: selectedBrandId,
        })
        .select()
    );

    throwOnSupabaseError(error);

    yield call(reserchScoresSaga, userId, false, [data[0].id]);
    yield put(fetchPrompts());
    yield put(addPromptSuccess());

    analytics.track('Prompt Added', {
      prompt_text: content.length > 50 ? content.substring(0, 50) + '...' : content,
    });
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "prompts.addPrompt",
      failureAction: addPromptFailure,
    });
  }
}

export function* changePromptStatusSaga(action: PayloadAction<ChangePromptStatusPayload>) {
  try {
    const { id, status } = action.payload;
    const userID: string | null = yield select(selectAuthAccountId);

    const { error } = yield call(() =>
      supabase
        .from(PROMPTS_TABLE_NAME)
        .update({ is_active: status })
        .eq('id', id)
        .eq('user_id', userID)
    );

    throwOnSupabaseError(error);

    yield put(fetchPrompts());

    analytics.track('Prompt Status Changed', {
      prompt_id: id,
      status: status ? 'active' : 'inactive'
    });
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "prompts.changePromptStatus",
      failureAction: changePromptStatusFailure,
    });
  }
}

export function* deletePromptSaga(action: PayloadAction<DeletePromptPayload>) {
  try {
    const { id } = action.payload;
    const userID: string | null = yield select(selectAuthAccountId);
    const { error } = yield call(() =>
      supabase
        .from(PROMPTS_TABLE_NAME)
        .update({ deleted_at: formatISO(new Date()) })
        .eq('id', id)
        .eq('user_id', userID)
    );

    throwOnSupabaseError(error);

    yield put(fetchPrompts());
    yield put(deletePromptSuccess());

    analytics.track('Prompt Deleted', {
      prompt_id: id
    });

  } catch (error) {
    yield* applySagaError(error, {
      operationName: "prompts.deletePrompt",
      failureAction: deletePromptFailure,
    });
  }
}


export function* promptsSaga() {
  yield takeEvery(fetchPrompts.type, fetchPromptsSaga);
  yield takeEvery(addPrompt.type, addPromptSaga);
  yield takeEvery(deletePrompt.type, deletePromptSaga);
  yield takeEvery(changePromptStatus.type, changePromptStatusSaga);
}